/** Marketing consent around registration: the wording that gets shown, and the
 * answer that gets recorded.
 *
 * Both registration entrypoints need exactly this — the OTP flow's registration
 * step and the social complete-registration page, the two callers of the shared
 * register form — and both used to carry their own copy of it. They had already
 * drifted once (each recorded a different set of fields, which is why the
 * service now settles the legal basis itself), so the second copy is the bug
 * waiting to happen, not the code.
 *
 * "Never in the way" (`specs/glossary.md`): no consent lookup or write may
 * block, break or undo a registration. Hence a load nobody waits on, and a
 * write that never throws.
 */

import type { ConsentService } from "../api/consent";
import { ApiHttpError } from "../api/errors";
import type { ConsentPanel } from "../components/consent";
import { track, type Tracker } from "../lib/analytics";
import { OTP } from "../lib/analyticsEvents";

/** Load the server-selected wording once and apply it to every panel on the
 * screen — the flow has two (the registration form's and the sign-in consent
 * step's), the social page one, a variant that hides consent none.
 *
 * Deliberately not awaited: a panel that never hears back keeps the built-in
 * copy it rendered with, which is the whole point of having a fallback. */
export function hydrateConsentPanels(
  consent: ConsentService,
  ...panels: Array<ConsentPanel | null>
): void {
  const present = panels.filter((panel): panel is ConsentPanel => !!panel);
  if (!present.length) return;
  void consent.loadPurpose().then((purpose) => {
    for (const panel of present) panel.applyPurpose(purpose);
  });
}

/** Record the visitor's answer as the now-authenticated user.
 *
 * Returns the choice that was recorded, or null when there was nothing to record
 * (no panel) or the write failed — so a caller reports an opt-in that actually
 * landed, and never one that didn't.
 *
 * Never throws, and never shows the visitor anything: by the time this runs the
 * account exists and they are signed in. Silent to *them* is not silent to us —
 * the failure is reported (see `reportConsentSaveFailure`) before this returns.
 * The sign-in consent *step* is the deliberate exception — an existing user
 * sitting on that screen can retry, so it calls the service directly, shows them
 * the error, and reports it itself.
 *
 * The report is awaited: every caller navigates immediately afterwards, and a
 * page load cancels an event the provider hasn't flushed yet. An unawaited
 * report here would go missing in exactly the case it exists to catch. */
export async function recordConsent(
  consent: ConsentService,
  panel: ConsentPanel | null,
  formContext: string,
  // The funnel's tracker, so a dropped opt-in is attributed to the same source
  // and variant as the rest of its funnel. Defaults to the bare module track.
  emit: Tracker = track,
  onFailure: (
    err: unknown,
    formContext: string,
  ) => void | Promise<void> = (err, ctx) =>
    reportConsentSaveFailure(err, ctx, emit),
): Promise<{ optedIn: boolean } | null> {
  if (!panel) return null;
  const optedIn = panel.box.checked;
  try {
    await consent.save(optedIn, formContext);
    return { optedIn };
  } catch (err) {
    try {
      await onFailure(err, formContext);
    } catch {
      /* "never in the way" outranks telling us about it: a reporter that
       * throws must not turn a dropped preference into a broken registration. */
    }
    return null;
  }
}

/** Report a consent write that didn't land — the one place that decides where
 * that goes.
 *
 * Two destinations, because they answer different questions:
 *
 *  - Segment (`OTP Consent Save Failed`), so the funnel reads Shown → Set |
 *    Save Failed. A drop shows up next to the conversion it belongs to, which
 *    is where the absence of `OTP Consent Set` was first noticed.
 *  - A structured `console.warn`, which survives the host page having no
 *    analytics provider at all — `track()` is a no-op there, so Segment alone
 *    would be silent on precisely the embeds we control least.
 *
 * Neither is an alerting channel. The one that already is: the edge logs every
 * proxied consent request with its status (`consent` in `src/lib/log.ts`), so
 * `event="consent" AND status>=400` in Workers Logs catches this server-side
 * even when no browser reports it. See docs/development.md.
 *
 * Never throws — `track()` never rejects, and the warn is guarded by the caller. */
export async function reportConsentSaveFailure(
  err: unknown,
  formContext: string,
  emit: Tracker = track,
): Promise<void> {
  const status = err instanceof ApiHttpError ? err.status : undefined;
  const message = err instanceof Error ? err.message : String(err);
  console.warn(
    JSON.stringify({
      event: "consent_save_failed",
      context: formContext,
      status,
      message,
    }),
  );
  await emit(OTP.CONSENT_SAVE_FAILED, { context: formContext, status });
}
